import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import type { TokenLinkState } from '@/pages/auth/TokenLinkPage.types';
import { doNothing } from '@/utils/doNothing';

// Only the App Link / Universal Link hosts, whose ownership the OS verifies against
// `/.well-known/`. A link from any other origin is not trusted to carry a token.
export const AUTH_LINK_HOSTS = ['app.khova.ai', 'app.development.khova.ai'];

const AUTH_LINK_PATH = /^\/auth\/(reset|invite)\/?$/;

export interface AuthLink {
  path: string;
  state: TokenLinkState;
}

/**
 * Maps a verified `https://app.khova.ai/auth/reset|invite?token_hash&type` link to its in-app
 * route. The token travels in router state, never in the in-app URL.
 */
export const getAuthLink = (url: URL): AuthLink | null => {
  if (url.protocol !== 'https:' || url.port || !AUTH_LINK_HOSTS.includes(url.hostname)) {
    return null;
  }
  const match = AUTH_LINK_PATH.exec(url.pathname);
  if (!match) {
    return null;
  }
  return {
    path: `/auth/${match[1]}`,
    state: { tokenHash: url.searchParams.get('token_hash') ?? '', type: url.searchParams.get('type') ?? '' },
  };
};

/**
 * Opens the password-reset and invite App/Universal Links on their pages. On native they reach
 * the app as an `appUrlOpen` event rather than a navigation.
 */
const useAuthLinks = (): void => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const listenerPromise = App.addListener('appUrlOpen', (event) => {
      const url = (() => {
        try {
          return new URL(event.url);
        } catch {
          return null;
        }
      })();

      const authLink = url ? getAuthLink(url) : null;
      if (authLink) {
        navigate(authLink.path, { state: authLink.state });
      }
    });

    return () => {
      listenerPromise.then((listener) => listener.remove()).catch(doNothing);
    };
  }, [navigate]);
};

export const AuthLinks = () => {
  useAuthLinks();
  return null;
};
